import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { FiMapPin, FiSearch, FiTarget, FiX } from 'react-icons/fi';

// Custom SVG Pin Icon for Drop Location
const dropPinIcon = L.divIcon({
  className: 'custom-drop-pin',
  html: `
    <div style="
      width: 44px;
      height: 44px;
      background: linear-gradient(135deg, #7b2fbe 0%, #4c1d95 100%);
      border: 3px solid #ffffff;
      border-radius: 50%;
      box-shadow: 0 8px 16px rgba(123, 47, 190, 0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 22px;
      cursor: pointer;
    ">
      🎯
    </div>
  `,
  iconSize: [44, 44],
  iconAnchor: [22, 22],
});

// Helper component to handle clicks on the map
function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Helper component to invalidate size on render so tiles load perfectly
function MapResizer() {
  const map = useMap();
  useEffect(() => {
    const timer1 = setTimeout(() => map.invalidateSize(), 100);
    const timer2 = setTimeout(() => map.invalidateSize(), 400);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [map]);
  return null;
}

// Helper component to programmatically re-center & fly map to selected location
function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.flyTo(center, 14, { duration: 1.2 });
      map.invalidateSize();
    }
  }, [center, map]);
  return null;
}

const DropMapPicker = ({ latitude, longitude, onLocationChange, claimRadius = 100 }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mapCenter, setMapCenter] = useState([latitude || 28.6139, longitude || 77.2090]); // Default Delhi
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (latitude && longitude) {
      setMapCenter([latitude, longitude]);
    }
  }, [latitude, longitude]);

  // Click outside listener to close autocomplete dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Live Rapido/Uber style Autocomplete Search Debounce (300ms)
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        // Fast Komoot Photon Autocomplete API (specifically optimized for live place search)
        const res = await fetch(
          `https://photon.komoot.io/api/?q=${encodeURIComponent(searchQuery.trim())}&limit=6`
        );
        const data = await res.json();

        if (data && data.features && data.features.length > 0) {
          const formatted = data.features.map((feat) => {
            const props = feat.properties || {};
            const coords = feat.geometry?.coordinates || [];
            const nameParts = [props.name, props.street, props.district, props.city, props.state, props.country]
              .filter(Boolean);
            const fullName = nameParts.join(', ');

            return {
              display_name: fullName || props.name || 'Location',
              lat: coords[1],
              lon: coords[0],
              city: props.city || props.state || '',
            };
          });

          setSuggestions(formatted);
          setShowDropdown(true);
        } else {
          // Fallback to Nominatim if Photon returns empty
          const fallbackRes = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery.trim())}&countrycodes=in&limit=5`
          );
          const fallbackData = await fallbackRes.json();
          if (fallbackData && fallbackData.length > 0) {
            setSuggestions(fallbackData.map(d => ({
              display_name: d.display_name,
              lat: parseFloat(d.lat),
              lon: parseFloat(d.lon),
            })));
            setShowDropdown(true);
          }
        }
      } catch (err) {
        console.error('Error fetching location suggestions:', err);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelectSuggestion = (item) => {
    const lat = typeof item.lat === 'string' ? parseFloat(item.lat) : item.lat;
    const lon = typeof item.lon === 'string' ? parseFloat(item.lon) : item.lon;

    if (!isNaN(lat) && !isNaN(lon)) {
      onLocationChange(lat, lon, item.display_name);
      setMapCenter([lat, lon]);
      setSearchQuery(item.display_name);
      setShowDropdown(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Live Rapido / Uber Style Location Search Input Box */}
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-purple-600 w-4 h-4" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
            placeholder="📍 Search location (e.g. Connaught Place, Patna Junction, Marine Drive)..."
            className="w-full pl-10 pr-10 py-3 bg-white border border-purple-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-100 shadow-md font-medium placeholder-slate-400"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => { setSearchQuery(''); setSuggestions([]); setShowDropdown(false); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded-full"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Live Autocomplete Suggestions Overlay Dropdown */}
        {showDropdown && suggestions.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-purple-100 rounded-2xl shadow-2xl z-[1000] overflow-hidden divide-y divide-slate-100 max-h-72 overflow-y-auto">
            <div className="px-4 py-2 bg-purple-50/60 text-[10px] font-bold text-purple-800 uppercase tracking-wider flex items-center justify-between">
              <span>Suggested Locations ({suggestions.length})</span>
              {isSearching && <span className="animate-pulse">Searching...</span>}
            </div>
            {suggestions.map((item, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSelectSuggestion(item)}
                className="w-full text-left px-4 py-3 hover:bg-purple-50/80 text-xs flex items-start gap-3 transition-all group"
              >
                <div className="p-2 bg-purple-100/70 group-hover:bg-purple-600 text-purple-700 group-hover:text-white rounded-xl shrink-0 transition-colors">
                  <FiMapPin className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-bold text-slate-900 block truncate group-hover:text-purple-900">
                    {item.display_name.split(',')[0]}
                  </span>
                  <span className="text-[11px] text-slate-500 block truncate">
                    {item.display_name}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Interactive Leaflet Map Container */}
      <div className="relative rounded-2xl overflow-hidden border border-slate-300 shadow-lg h-96 bg-slate-100 z-10">
        <MapContainer
          center={mapCenter}
          zoom={14}
          scrollWheelZoom={true}
          style={{ width: '100%', height: '100%', minHeight: '384px' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MapResizer />
          <MapRecenter center={mapCenter} />

          <MapClickHandler
            onLocationSelect={(lat, lng) => {
              onLocationChange(lat, lng);
            }}
          />

          {latitude && longitude && (
            <>
              {/* 10km Outer Search Zone Circle */}
              <Circle
                center={[latitude, longitude]}
                radius={10000}
                pathOptions={{
                  color: '#7b2fbe',
                  fillColor: '#7b2fbe',
                  fillOpacity: 0.08,
                  weight: 1.5,
                  dashArray: '6, 6',
                }}
              />

              {/* Inner Claim Radius Circle */}
              <Circle
                center={[latitude, longitude]}
                radius={claimRadius}
                pathOptions={{
                  color: '#dc2626',
                  fillColor: '#ef4444',
                  fillOpacity: 0.3,
                  weight: 2,
                }}
              />

              {/* Target Location Pin Marker */}
              <Marker position={[latitude, longitude]} icon={dropPinIcon} />
            </>
          )}
        </MapContainer>

        {/* Map Overlay Badge Info */}
        <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-md px-3.5 py-2 rounded-xl border border-slate-200 shadow-md z-[400] text-[11px] text-slate-700 flex items-center gap-2 font-semibold">
          <FiTarget className="w-3.5 h-3.5 text-purple-700 animate-pulse" />
          <span>Click anywhere on the map or search above to place Drop GPS Coordinates</span>
        </div>
      </div>

      {/* Selected Coordinates Display */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
          <span className="text-[10px] font-bold text-slate-500 uppercase block">Latitude</span>
          <span className="font-mono font-bold text-purple-900 text-sm">{latitude ? latitude.toFixed(6) : 'Not Selected'}</span>
        </div>
        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
          <span className="text-[10px] font-bold text-slate-500 uppercase block">Longitude</span>
          <span className="font-mono font-bold text-purple-900 text-sm">{longitude ? longitude.toFixed(6) : 'Not Selected'}</span>
        </div>
      </div>
    </div>
  );
};

export default DropMapPicker;
